import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScreenQuestion } from '../../../features/flow/types';
import { formatCurrency } from '../../../i18n/formatters';
import { StripePaymentBlock } from './StripePaymentBlock';

function formatQuoteEur(value: unknown, language: string): string {
  const currency = 'EUR';
  if (value === null || value === undefined || value === '') {
    return formatCurrency(0, language, currency);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatCurrency(value, language, currency);
  }
  const s = String(value).trim();
  if (s) return s;
  return formatCurrency(0, language, currency);
}

function getLineRaw(q: ScreenQuestion, answers: Record<string, unknown>): unknown {
  const src = q.source_field?.trim();
  return q.current_value ?? answers[q.id] ?? (src ? answers[src] : undefined);
}

function lineHasValue(raw: unknown): boolean {
  return raw !== null && raw !== undefined && String(raw).trim() !== '';
}

/** Figma: Surface-page-secondary + Stroke + shadow token */
const QUOTE_CARD_SURFACE =
  'box-border flex w-full max-w-[576px] flex-col rounded-lg border border-solid border-[#E2E8F0] ' +
  'bg-[#F9FAFB] p-6 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]';

/**
 * Merged quote + payment: fee details (net / IVA / gross) per Figma, then Stripe.
 */
export function PersonalizedTaxQuoteReadyScreenBody({
  questions,
  answers,
  sessionId,
  onPaymentNext,
  onBack,
  isSubmitting,
}: {
  questions: ScreenQuestion[];
  answers: Record<string, unknown>;
  sessionCountry?: string | null;
  sessionId: string;
  onPaymentNext: (patch: Record<string, unknown>) => Promise<void> | void;
  onBack: () => void;
  isSubmitting: boolean;
}) {
  const { t, i18n } = useTranslation('screens', { keyPrefix: 'your_personalized_tax_quote_is_ready' });

  const serviceQ = useMemo(
    () => questions.find((q) => q.source_field === 'tax_quote_service_cost_eur'),
    [questions]
  );
  const ivaQ = useMemo(
    () => questions.find((q) => q.source_field === 'tax_quote_iva_eur'),
    [questions]
  );
  const totalFeeQ = useMemo(
    () => questions.find((q) => q.id === 'personalized_tax_quote_total'),
    [questions]
  );

  const feeRows = useMemo(() => {
    const rows: { label: string; value: string; strong?: boolean }[] = [];
    if (serviceQ) {
      const raw = getLineRaw(serviceQ, answers);
      if (lineHasValue(raw)) {
        rows.push({
          label: (serviceQ.question_text ?? '').trim() || t('fee.service_cost', { defaultValue: 'Service cost' }),
          value: formatQuoteEur(raw, i18n.language),
        });
      }
    }
    if (ivaQ) {
      const raw = getLineRaw(ivaQ, answers);
      if (lineHasValue(raw)) {
        rows.push({
          label: (ivaQ.question_text ?? '').trim() || t('fee.iva', { defaultValue: 'IVA (21%)' }),
          value: formatQuoteEur(raw, i18n.language),
        });
      }
    }
    if (totalFeeQ) {
      const raw = getLineRaw(totalFeeQ, answers);
      if (lineHasValue(raw)) {
        rows.push({
          label: (totalFeeQ.question_text ?? '').trim() || t('fee.total', { defaultValue: 'Total amount to pay' }),
          value: formatQuoteEur(raw, i18n.language),
          strong: true,
        });
      }
    }
    return rows;
  }, [serviceQ, ivaQ, totalFeeQ, answers, i18n.language, t]);

  const feeTitle = t('fee.section_title', { defaultValue: 'Tax declaring fee details' });
  const feeDescription = t('fee.description', {
    defaultValue:
      'One price covers everything. We pull your tax data directly from AEAT, check it for errors, and find every deduction you\'re entitled to. Then file your return officially on your behalf. No back and forth, no forms to figure out, no risk of missing something.',
  });

  return (
    <div className="flex w-full max-w-[576px] flex-col items-stretch gap-8">
      <div className={[QUOTE_CARD_SURFACE, 'gap-8'].join(' ')}>
        <div>
          <h2 className="text-base font-semibold text-[#131315] m-0">{feeTitle}</h2>
          <p className="mt-2 mb-0 text-sm font-normal text-[#525353] leading-relaxed">{feeDescription}</p>
        </div>
        <div className="flex flex-col gap-2 border-t border-[#E2E8F0] pt-2">
          {feeRows.map((row) => (
            <React.Fragment key={row.label}>
              {row.strong ? (
                <div className="w-full border-t border-[#E2E8F0] pt-3 mt-1" aria-hidden />
              ) : null}
              <div className="flex w-full flex-row items-center justify-between gap-4 text-sm">
                <span className="font-normal text-[#525353] shrink min-w-0">{row.label}</span>
                <span
                  className={
                    row.strong
                      ? 'font-semibold text-[#131315] text-right tabular-nums shrink-0'
                      : 'font-medium text-[#131315] text-right tabular-nums shrink-0'
                  }
                >
                  {row.value}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className={[QUOTE_CARD_SURFACE, 'gap-8'].join(' ')}>
        <h2 className="text-base font-semibold text-[#131315] m-0">
          {t('payment_method.title', { defaultValue: 'Payment method' })}
        </h2>
        <StripePaymentBlock
          sessionId={sessionId}
          onNext={onPaymentNext}
          onBack={onBack}
          isSubmitting={isSubmitting}
          showInlineFeeSummary={false}
        />
      </div>
    </div>
  );
}
